import type {
  ResolutionStatus,
  VisioReviewActivityHeatmap,
  VisioReviewInteractionsResponse,
  VisioReviewInteractionsSortBy,
  VisioReviewOperatorDetail,
  VisioReviewOperatorInteractionsResponse,
  VisioReviewOperatorsResponse,
  VisioReviewOperatorsSortBy,
  VisioReviewSortDir,
  VisioReviewStoreDetail,
  VisioReviewStoreInteractionsResponse,
  VisioReviewStoreOperatorsResponse,
  VisioReviewStoresResponse,
  VisioReviewStoresSortBy,
} from '@gozoki/api-types'
import { gozokiApiWithAuth } from '../gozokiApi'

export interface FetchVisioReviewInteractionsParams {
  dateStart?: string
  dateEnd: string
  page?: number
  pageSize?: number
  storeId?: number
  operatorId?: number
  minGrade?: number
  maxGrade?: number
  noGrade?: boolean
  notValidated?: boolean
  onlyVerified?: boolean
  kioskTypes?: string
  resolutionStatus?: ResolutionStatus
  search?: string
  sortBy?: VisioReviewInteractionsSortBy
  sortDir?: VisioReviewSortDir
}

export const fetchVisioReviewInteractions = async (params: FetchVisioReviewInteractionsParams) => {
  const res = await gozokiApiWithAuth.get<VisioReviewInteractionsResponse>(
    '/visio-reviews/interactions',
    { params },
  )
  return res.data
}

export interface FetchVisioReviewOperatorsParams {
  dateStart?: string
  dateEnd: string
  previousStart?: string
  previousEnd?: string
  page?: number
  pageSize?: number
  search?: string
  sortBy?: VisioReviewOperatorsSortBy
  sortDir?: VisioReviewSortDir
  kioskTypes?: string
  onlyVerified?: boolean
}

export const fetchVisioReviewOperators = async (params: FetchVisioReviewOperatorsParams) => {
  const res = await gozokiApiWithAuth.get<VisioReviewOperatorsResponse>(
    '/visio-reviews/operators',
    { params },
  )
  return res.data
}

export interface FetchVisioReviewActivityHeatmapParams {
  dateStart?: string
  dateEnd: string
  kioskTypes?: string
  onlyVerified?: boolean
}

export const fetchVisioReviewOperatorActivityHeatmap = async (
  adminId: number,
  params: FetchVisioReviewActivityHeatmapParams,
) => {
  const res = await gozokiApiWithAuth.get<VisioReviewActivityHeatmap>(
    `/visio-reviews/operators/${adminId}/activity-heatmap`,
    { params },
  )
  return res.data
}

export const fetchVisioReviewStoreActivityHeatmap = async (
  storeId: number,
  params: FetchVisioReviewActivityHeatmapParams,
) => {
  const res = await gozokiApiWithAuth.get<VisioReviewActivityHeatmap>(
    `/visio-reviews/stores/${storeId}/activity-heatmap`,
    { params },
  )
  return res.data
}

export const fetchVisioReviewOperatorDetail = async (
  adminId: number,
  params: { dateStart?: string; dateEnd: string; kioskTypes?: string; onlyVerified?: boolean },
) => {
  const res = await gozokiApiWithAuth.get<VisioReviewOperatorDetail>(
    `/visio-reviews/operators/${adminId}`,
    { params },
  )
  return res.data
}

export interface FetchVisioReviewOperatorInteractionsParams {
  dateStart?: string
  dateEnd: string
  page?: number
  pageSize?: number
  kioskTypes?: string
  onlyVerified?: boolean
}

export const fetchVisioReviewOperatorInteractions = async (
  adminId: number,
  params: FetchVisioReviewOperatorInteractionsParams,
) => {
  const res = await gozokiApiWithAuth.get<VisioReviewOperatorInteractionsResponse>(
    `/visio-reviews/operators/${adminId}/interactions`,
    { params },
  )
  return res.data
}

export interface FetchVisioReviewStoreListParams {
  dateStart?: string
  dateEnd: string
  previousStart?: string
  previousEnd?: string
  page?: number
  pageSize?: number
  search?: string
  sortBy?: VisioReviewStoresSortBy
  sortDir?: VisioReviewSortDir
  kioskTypes?: string
  /** Filter on aggregated avg_grade (inclusive). NULL avg_grades are excluded. */
  minAvgGrade?: number
  /** Filter on aggregated avg_grade (exclusive upper bound). */
  maxAvgGrade?: number
  onlyVerified?: boolean
}

export const fetchVisioReviewStoreList = async (params: FetchVisioReviewStoreListParams) => {
  const res = await gozokiApiWithAuth.get<VisioReviewStoresResponse>('/visio-reviews/stores', {
    params,
  })
  return res.data
}

export const fetchVisioReviewStoreDetail = async (
  storeId: number,
  params: { dateStart?: string; dateEnd: string; kioskTypes?: string; onlyVerified?: boolean },
) => {
  const res = await gozokiApiWithAuth.get<VisioReviewStoreDetail>(
    `/visio-reviews/stores/${storeId}`,
    { params },
  )
  return res.data
}

export interface FetchVisioReviewStoreInteractionsParams {
  dateStart?: string
  dateEnd: string
  page?: number
  pageSize?: number
  kioskTypes?: string
  onlyVerified?: boolean
}

export const fetchVisioReviewStoreInteractions = async (
  storeId: number,
  params: FetchVisioReviewStoreInteractionsParams,
) => {
  const res = await gozokiApiWithAuth.get<VisioReviewStoreInteractionsResponse>(
    `/visio-reviews/stores/${storeId}/interactions`,
    { params },
  )
  return res.data
}

export interface FetchVisioReviewStoreOperatorsParams {
  dateStart?: string
  dateEnd: string
  kioskTypes?: string
  page?: number
  pageSize?: number
  onlyVerified?: boolean
}

export const fetchVisioReviewStoreOperators = async (
  storeId: number,
  params: FetchVisioReviewStoreOperatorsParams,
) => {
  const res = await gozokiApiWithAuth.get<VisioReviewStoreOperatorsResponse>(
    `/visio-reviews/stores/${storeId}/operators`,
    { params },
  )
  return res.data
}

export interface FetchVisioReviewInteractionsToEvaluateParams {
  dateStart?: string
  dateEnd?: string
  page?: number
  pageSize?: number
  sortBy?: VisioReviewInteractionsSortBy
  sortDir?: VisioReviewSortDir
  storeId?: number
  operatorId?: number
  minGrade?: number
  maxGrade?: number
  noGrade?: boolean
  notValidated?: boolean
  kioskTypes?: string
  search?: string
}

export const fetchVisioReviewInteractionsToEvaluate = async (
  params: FetchVisioReviewInteractionsToEvaluateParams,
) => {
  const res = await gozokiApiWithAuth.get<VisioReviewInteractionsResponse>(
    '/visio-reviews/to-evaluate',
    { params },
  )
  return res.data
}
